<template>
  <v-card class="h-100">
    <v-card-text class="py-6 px-4">
      <v-checkbox
        v-model="localValue.wifiPhishingEnabled"
        data-testid="device-settings-wifi-phishing-checkbox"
        :ripple="false"
        class="mt-1"
        :disabled="actionNotAllowed"
      >
        <template #label>
          <div class="subtitle1">
            {{ $t("devicesSettings.settingsCard.wifiPhishing") }}
          </div>
        </template>
      </v-checkbox>
      <div class="ml-7">
        <div class="body2 text-indigo-medium">
          {{ $t("devicesSettings.settingsCard.wifiPhishingDescription") }}
        </div>
        <template v-if="localValue.wifiPhishingEnabled">
          <v-autocomplete
            v-model="localValue.wifiPhishingLabels"
            class="mt-4"
            :items="labels"
            :label="deviceLabelsLabel(localValue.wifiPhishingLabels?.length)"
            :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
            :disabled="actionNotAllowed"
            variant="outlined"
            multiple
            return-object
            data-testid="device-settings-wifi-phishing-labels"
            item-value="id"
            item-title="name"
            hide-details="auto"
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                :closable="true"
                size="default"
                :color="item.raw.color"
                close-icon="$x"
                label
                rounded="3"
                variant="flat"
                :text="item.raw.name"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ item.raw.name }}
                </span>
              </v-chip>
            </template>

            <template #item="{ item, props }">
              <v-list-item v-bind="props" title="" class="label-popup-block">
                <div class="d-flex align-center">
                  <span
                    class="color-preview mr-2 mb-0"
                    :style="{ 'background-color': item.raw.color }"
                  />
                  <span
                    :data-testid="`device-settings-wifi-phishing-${kebabCase(
                      item.raw.name
                    )}-select-item`"
                  >
                    {{ item.raw.name }}
                  </span>
                </div>
              </v-list-item>
            </template>
          </v-autocomplete>
          <div class="d-flex align-center pl-4 mt-1">
            <v-icon class="mr-1" size="18" icon="$circleCheck" />
            <span class="caption text-green-dark">
              {{ $t("devicesSettings.settingsCard.labelsFieldHint") }}
            </span>
          </div>
        </template>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, toRefs } from "vue";
import type { PropType } from "vue";
import kebabCase from "lodash/kebabCase";
import type { DeviceLabel, WifiPhishingSettings } from "@/_store/devices-settings.module";
import { useI18n } from "vue-i18n";

import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  props: {
    settings: {
      type: Object as PropType<WifiPhishingSettings>,
      required: true,
    },
    labels: {
      type: Array as PropType<DeviceLabel[]>,
      required: true,
    },
    actionNotAllowed: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:settings"],

  setup(props, { emit }) {
    const i18n = useI18n();
    const { settings } = toRefs(props);
    const localValue = useTwoWayBinding(settings, (value) => {
      emit("update:settings", value);
    });

    const deviceLabelsLabel = (labelsLength: number) => {
      return labelsLength ? i18n.t("devicesSettings.settingsCard.deviceLabelsLabel") : "";
    };

    return {
      kebabCase,
      deviceLabelsLabel,
      localValue,
    };
  },
});
</script>

<style scoped lang="scss">
.label-popup-block {
  .v-list-item__content {
    display: flex;
    align-items: center;
  }
}
</style>
